import { useEffect, useState } from "react";
import { getHunters } from "../api/hunters";
import type { HuntersReadModel } from "../types/api";
import { subscribeLive } from "../aim/liveBus";

const apiBase = (
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_AIM_API_BASE || ""
).replace(/\/$/, "");

const empty: HuntersReadModel = {
  connectedParticipants: 0,
  trackingParticipants: 0,
  activeParticipants: 0,
  detectionsPerMinute: 0,
  bands: {},
  closestParticipants: [],
};

// HUNTERS panel data: the anonymous #TodosSomosCICLOPE crowd — never a
// source of authoritative zone (see docs/PUBLIC_PARTICIPATION.md).
// Refetches on the same shared SSE-or-poll signal as useNetwork/useCarrier
// (apps/portal/src/aim/liveBus.ts), same resilience pattern as useCarrier:
// a failed fetch keeps the last known snapshot on screen.
export function useHunters(targetId: string, enabled: boolean) {
  const [data, setData] = useState<HuntersReadModel>(empty);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled || !targetId) {
      setData(empty);
      setError(false);
      return;
    }
    let stopped = false;
    const abort = new AbortController();
    async function refresh() {
      try {
        const next = await getHunters(targetId, abort.signal);
        if (!stopped) {
          setData(next);
          setError(false);
        }
      } catch {
        if (!stopped) setError(true);
      }
    }
    void refresh();
    const unsubscribe = subscribeLive(apiBase, () => void refresh());
    return () => {
      stopped = true;
      abort.abort();
      unsubscribe();
    };
  }, [targetId, enabled]);

  return { data, error };
}
